import type { ReactNode } from 'react';
import { Banner } from './Banner.js';
import { Button } from './Button.js';
import { Text } from './Text.js';
import { Icon } from './Icon.js';
import { ICONS } from '../icons/index.js';

/**
 * **`ErrorBanner`**（§5.5 / §4.7 の「接続が切れた」）。
 *
 * ## SSE は演出、真実は DynamoDB（§9 / §4.7）
 *
 * §4.7:「**接続が切れた**: `done` を受け取れない →
 * **`GET /api/turn/{turnId}`**。**SSE は演出、真実は DynamoDB。**
 * 『**開き直すと続きから戻れます**』と書く」。
 *
 * **これは M15 のインフラ再評価で「切断からの回復の読み経路」として
 * 挙がった論点そのもの**である（fetch ストリームが切れると
 * **サーバはターン完了済み・クライアントは発話未受領**になる）。
 *
 * ## `recoverable` と `fatal` を分ける
 *
 * **再取得できるものと、できないものを同じ見た目にしない** —
 * 押しても直らないボタンを出すと、**押し続けさせることになる**。
 */
export interface ErrorBannerProps {
  readonly kind: 'recoverable' | 'fatal';
  readonly message: string;
  /** `recoverable` のときだけ渡す（**再取得の導線**） */
  readonly onRetry?: () => void;
}

export function ErrorBanner({ kind, message, onRetry }: ErrorBannerProps): ReactNode {
  return (
    <Banner variant="danger" leading={<Icon svg={ICONS.attention} size={20} />}>
      <Text variant="note">{message}</Text>
      {kind === 'recoverable' && onRetry !== undefined ? (
        <>
          {/* **「開き直すと続きから戻れます」**（§4.7 の文言） */}
          <Text variant="note">開き直すと続きから戻れます。</Text>
          <Button variant="subtle" size="sm" onClick={onRetry}>
            もう一度取得する
          </Button>
        </>
      ) : null}
    </Banner>
  );
}
